// src/pages/EmployeeTasks.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../Context/AuthContext";
import { db, storage } from "../firebase/config";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  where,
  limit,
  updateDoc,
  addDoc, // ✅ ADD THIS
  serverTimestamp,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { DateTime } from "luxon";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../styles/EmployeeTasks.css";
import { Link } from "react-router-dom";

// Only toast for high-signal events.
// Everything else should be quiet (use inline UI hints instead).
const notify = {
  success: (msg) => toast.success(msg),
  error: (msg) => toast.error(msg),
  info: (msg) => toast.info(msg),

  // micro actions: intentionally no toast
  micro: () => {},
};

const STATUSES = ["open", "in_progress", "completed", "archived"]; // completed = submitted-for-review

export default function EmployeeTasks() {
  const { user, loading } = useAuth();

  const [companyId, setCompanyId] = useState(null);
  const [companyTimeZone, setCompanyTimeZone] = useState("UTC");

  const [loadingInit, setLoadingInit] = useState(true);
  const [loadingTasks, setLoadingTasks] = useState(true);

  const [tasks, setTasks] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");

  // ✅ Modal state
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState(null);

  // local previews (per task): [{id, file, url, status}]
  // status: "ready" | "uploading" | "uploaded" | "error"
  const [localPreviews, setLocalPreviews] = useState({}); // taskId -> preview[]
  const [uploadingProof, setUploadingProof] = useState({}); // taskId -> boolean

  // ✅ Single comment field (draft per task)
  const [draftComments, setDraftComments] = useState({}); // taskId -> string
  const [savingComment, setSavingComment] = useState({}); // taskId -> boolean
  const [priorityFilter, setPriorityFilter] = useState("all");

  // Keep refs so we can reset file inputs
  const fileInputRefs = useRef({}); // taskId -> input element

  // ─────────────────────────────────────────────────────────────
  // Bootstrap: get companyId + timezone
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const run = async () => {
      if (!user?.uid) {
        setLoadingInit(false);
        return;
      }

      setLoadingInit(true);
      try {
        let cid = user?.companyId || null;

        if (!cid) {
          const userSnap = await getDoc(doc(db, "users", user.uid));
          cid = userSnap.data()?.companyId || null;
        }

        if (!cid) {
          toast.error("Company not found for this employee.");
          setCompanyId(null);
          return;
        }

        setCompanyId(cid);

        const companySnap = await getDoc(doc(db, "companies", cid));
        const companyData = companySnap.data() || {};
        setCompanyTimeZone(companyData.timeZone || "UTC");
      } catch (e) {
        console.error("EmployeeTasks init failed:", e);
        toast.error("Unable to load your tasks.");
      } finally {
        setLoadingInit(false);
      }
    };

    if (!loading) run();
  }, [user?.uid, user?.companyId, loading]);

  // ─────────────────────────────────────────────────────────────
  // Live tasks subscription: tasks assigned to this employee
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!companyId || !user?.uid) return;

    setLoadingTasks(true);

    const baseRef = collection(db, `companies/${companyId}/tasks`);
    const qRef = query(
      baseRef,
      where("assigneeId", "==", user.uid),
      limit(200)
    );

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // ✅ Most recently assigned (created) on top
        // tie-breaker: due date asc
        list.sort((a, b) => {
          const assignedDiff = getAssignedMs(b) - getAssignedMs(a);
          if (assignedDiff !== 0) return assignedDiff;

          return String(a.dueDateISO || "").localeCompare(
            String(b.dueDateISO || "")
          );
        });

        setTasks(list);

        // Initialize comment draft per task only once (so you don't overwrite typing)
        setDraftComments((prev) => {
          const next = { ...prev };
          list.forEach((t) => {
            if (next[t.id] === undefined)
              next[t.id] = t.completionComment || "";
          });
          return next;
        });

        setLoadingTasks(false);
      },
      (err) => {
        console.error("EmployeeTasks snapshot error:", err);
        setLoadingTasks(false);
      }
    );

    return () => unsub();
  }, [companyId, user?.uid]);

  // Keep activeTask in sync even while modal is open
  const activeTask = useMemo(() => {
    if (!activeTaskId) return null;
    return tasks.find((t) => t.id === activeTaskId) || null;
  }, [tasks, activeTaskId]);

  useEffect(() => {
    // if task disappears (deleted/archived), close modal safely
    if (showTaskModal && activeTaskId && !activeTask) {
      setShowTaskModal(false);
      setActiveTaskId(null);
    }
  }, [showTaskModal, activeTaskId, activeTask]);

  // ─────────────────────────────────────────────────────────────
  // Cleanup preview object URLs
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      Object.values(localPreviews).forEach((arr) => {
        (arr || []).forEach((p) => {
          if (p?.url?.startsWith("blob:")) URL.revokeObjectURL(p.url);
        });
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleTasks = useMemo(() => {
    let list = tasks.filter((t) => t.archived !== true);

    if (statusFilter !== "all") {
      list = list.filter((t) => (t.status || "open") === statusFilter);
    }

    if (priorityFilter !== "all") {
      list = list.filter(
        (t) => String(t.priority || "").toLowerCase() === priorityFilter
      );
    }

    return list;
  }, [tasks, statusFilter, priorityFilter]);

  const openTaskModal = (taskId) => {
    setActiveTaskId(taskId);
    setShowTaskModal(true);
  };

  const closeTaskModal = () => {
    setShowTaskModal(false);
    setActiveTaskId(null);
  };

  // Firestore Timestamp-safe -> ms (we treat createdAt as Assigned)
  const getAssignedMs = (task) => {
    const v = task?.createdAt || null;

    // Firestore Timestamp
    if (v && typeof v.toMillis === "function") return v.toMillis();

    // Date
    if (v instanceof Date) return v.getTime();

    // ISO string fallback (if you ever store strings)
    if (typeof v === "string") {
      const dt = DateTime.fromISO(v);
      return dt.isValid ? dt.toMillis() : 0;
    }

    return 0;
  };

  const formatAssigned = (task) => {
    const v = task?.createdAt;
    if (!v) return "—";

    try {
      // Firestore Timestamp
      if (typeof v?.toDate === "function") {
        return DateTime.fromJSDate(v.toDate(), {
          zone: companyTimeZone,
        }).toFormat("MMM dd, yyyy");
      }

      // Date
      if (v instanceof Date) {
        return DateTime.fromJSDate(v, { zone: companyTimeZone }).toFormat(
          "MMM dd, yyyy"
        );
      }

      // ISO
      if (typeof v === "string") {
        return DateTime.fromISO(v, { zone: companyTimeZone }).toFormat(
          "MMM dd, yyyy"
        );
      }

      return "—";
    } catch {
      return "—";
    }
  };

  const formatDue = (task) => {
    const iso = task?.dueDateISO;
    if (!iso) return "—";
    try {
      return DateTime.fromISO(iso, { zone: companyTimeZone }).toFormat(
        "MMM dd, yyyy"
      );
    } catch {
      return iso;
    }
  };

  const isOverdue = (task) => {
    if (!task?.dueDateISO) return false;
    const now = DateTime.now()
      .setZone(companyTimeZone)
      .startOf("day");
    const due = DateTime.fromISO(task.dueDateISO, {
      zone: companyTimeZone,
    }).startOf("day");

    return (
      due < now &&
      task.status !== "archived" &&
      task.reviewStatus !== "approved"
    );
  };

  // ─────────────────────────────────────────────────────────────
  // ✅ Checklist helpers (employee-side)
  // checklist stored on task doc:
  // checklistEnabled: boolean
  // checklist: [{ id, text, isDone, doneAt, doneBy }]
  // ─────────────────────────────────────────────────────────────
  const normalizeChecklist = (task) => {
    const enabled = !!task?.checklistEnabled;
    const items = Array.isArray(task?.checklist) ? task.checklist : [];
    return { enabled, items };
  };

  const getChecklistProgress = (items) => {
    const total = items.length;
    const done = items.filter((it) => !!it.isDone).length;
    return { total, done, allDone: total > 0 && done === total };
  };

  const toggleChecklistItem = async (task, itemId) => {
    if (!companyId || !user?.uid || !task?.id) return;

    const reviewStatus = task.reviewStatus || "not_submitted";
    const lockedForReview =
      reviewStatus === "submitted" || reviewStatus === "approved";
    if (lockedForReview) {
      toast.error("This task is under review and cannot be changed right now.");
      return;
    }

    const { enabled, items } = normalizeChecklist(task);
    if (!enabled || !items.length) return;

    const idx = items.findIndex((it) => it.id === itemId);
    if (idx === -1) return;

    const current = items[idx];
    const nextDone = !current.isDone;

    const nextItems = items.map((it) =>
      it.id === itemId
        ? {
            ...it,
            isDone: nextDone,
            doneAt: nextDone ? DateTime.now().toISO() : null,
            doneBy: nextDone ? user.uid : null,
          }
        : it
    );

    try {
      await updateDoc(doc(db, `companies/${companyId}/tasks/${task.id}`), {
        checklist: nextItems,
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error("toggleChecklistItem failed:", e);
      notify.error("Could not update checklist. Try again.");
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Helpers for photos
  // ─────────────────────────────────────────────────────────────
  const normalizeExistingPhotos = (task) => {
    const arr = Array.isArray(task?.completionPhotos)
      ? task.completionPhotos
      : [];

    // Backwards compatibility (old single URL)
    if (!arr.length && task?.completionPhotoURL) {
      return [
        {
          url: task.completionPhotoURL,
          path: task.completionPhotoPath || "",
          uploadedAt: task.completionPhotoUploadedAt || null,
          uploadedBy: task.completedBy || task.createdBy || "",
          name: "proof",
          size: null,
        },
      ];
    }
    return arr;
  };

  // ─────────────────────────────────────────────────────────────
  // Comment save (single field)
  // ─────────────────────────────────────────────────────────────
  const saveComment = async (task) => {
    if (!companyId || !user?.uid || !task?.id) return;

    const taskId = task.id;
    const raw = draftComments[taskId] ?? "";
    const trimmed = String(raw).trim();

    if (trimmed.length > 500) {
      toast.error("Comment is too long (max 500 characters).");
      return;
    }

    setSavingComment((prev) => ({ ...prev, [taskId]: true }));

    try {
      await updateDoc(doc(db, `companies/${companyId}/tasks/${taskId}`), {
        completionComment: trimmed || null,
        completionCommentUpdatedAt: serverTimestamp(),
        completionCommentUpdatedBy: user.uid,
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error("saveComment failed:", e);
      notify.error("Could not save comment.");
    } finally {
      setSavingComment((prev) => ({ ...prev, [taskId]: false }));
    }
  };

  const validateImages = (files) => {
    const maxBytes = 8 * 1024 * 1024;
    const valid = [];

    for (const file of files) {
      if (!file) continue;
      if (!file.type?.startsWith("image/")) {
        toast.error("Only image files are allowed (jpg, png, etc).");
        continue;
      }
      if (file.size > maxBytes) {
        toast.error("One of the images is too large. Please upload under 8MB.");
        continue;
      }
      valid.push(file);
    }

    return valid;
  };

  // ─────────────────────────────────────────────────────────────
  // Auto-upload proof photos
  // ─────────────────────────────────────────────────────────────
  const onSelectProofFiles = async (task, fileList) => {
    if (!companyId || !user?.uid || !task?.id) return;

    const taskId = task.id;

    const reviewStatus = task.reviewStatus || "not_submitted";
    const lockedForReview =
      reviewStatus === "submitted" || reviewStatus === "approved";
    if (lockedForReview) {
      toast.error(
        "This task is submitted for review and cannot be modified right now."
      );
      return;
    }

    const files = validateImages(Array.from(fileList || []));
    if (!files.length) return;

    const previews = files.map((file) => ({
      id: `${taskId}_${file.name}_${file.size}_${Date.now()}_${Math.random()
        .toString(16)
        .slice(2)}`,
      file,
      url: URL.createObjectURL(file),
      status: "uploading",
      error: null,
    }));

    setLocalPreviews((prev) => ({
      ...prev,
      [taskId]: [...(prev[taskId] || []), ...previews],
    }));

    setUploadingProof((prev) => ({ ...prev, [taskId]: true }));

    try {
      const uploaded = [];

      for (const p of previews) {
        try {
          const extGuess = (p.file.name || "").split(".").pop() || "jpg";
          const safeExt = String(extGuess)
            .toLowerCase()
            .replace(/[^a-z0-9]/g, "");
          const filename = `${user.uid}_${Date.now()}_${Math.random()
            .toString(16)
            .slice(2)}.${safeExt || "jpg"}`;

          const path = `companies/${companyId}/task_proofs/${taskId}/${filename}`;
          const storageRef = ref(storage, path);

          await uploadBytes(storageRef, p.file);
          const url = await getDownloadURL(storageRef);

          uploaded.push({
            url,
            path,
            uploadedAt: DateTime.now().toISO(),
            uploadedBy: user.uid,
            name: p.file.name || "proof",
            size: p.file.size || null,
          });

          setLocalPreviews((prev) => ({
            ...prev,
            [taskId]: (prev[taskId] || []).map((x) =>
              x.id === p.id ? { ...x, status: "uploaded" } : x
            ),
          }));
        } catch (err) {
          console.error("Upload single proof failed:", err);
          setLocalPreviews((prev) => ({
            ...prev,
            [taskId]: (prev[taskId] || []).map((x) =>
              x.id === p.id ? { ...x, status: "error", error: "Failed" } : x
            ),
          }));
        }
      }

      if (!uploaded.length) {
        toast.error("Could not upload the selected photo(s). Try again.");
        return;
      }

      const existing = normalizeExistingPhotos(task);
      const merged = [...existing, ...uploaded];

      await updateDoc(doc(db, `companies/${companyId}/tasks/${taskId}`), {
        completionPhotos: merged,

        // legacy sync
        completionPhotoURL: merged[0]?.url || null,
        completionPhotoPath: merged[0]?.path || null,
        completionPhotoUploadedAt: serverTimestamp(),

        updatedAt: serverTimestamp(),
      });

      clearUploadedLocalPreviews(
        taskId,
        previews.map((p) => p.id)
      );
    } catch (e) {
      console.error("Auto upload failed:", e);
      toast.error("Could not upload photos. Try again.");
    } finally {
      setUploadingProof((prev) => ({ ...prev, [taskId]: false }));
      if (fileInputRefs.current[taskId])
        fileInputRefs.current[taskId].value = "";
    }
  };

  // Remove uploaded photo
  const removeUploadedPhoto = async (task, photoIndex) => {
    if (!companyId || !user?.uid || !task?.id) return;

    const taskId = task.id;

    const reviewStatus = task.reviewStatus || "not_submitted";
    const lockedForReview =
      reviewStatus === "submitted" || reviewStatus === "approved";
    if (lockedForReview) {
      toast.error(
        "This task is submitted for review and cannot be modified right now."
      );
      return;
    }

    const existing = normalizeExistingPhotos(task);
    const target = existing[photoIndex];
    if (!target) return;

    setUploadingProof((prev) => ({ ...prev, [taskId]: true }));

    try {
      if (target.path) {
        try {
          await deleteObject(ref(storage, target.path));
        } catch (err) {
          console.warn("deleteObject failed (non-blocking):", err);
        }
      }

      const next = existing.filter((_, i) => i !== photoIndex);

      await updateDoc(doc(db, `companies/${companyId}/tasks/${taskId}`), {
        completionPhotos: next,
        completionPhotoURL: next[0]?.url || null,
        completionPhotoPath: next[0]?.path || null,
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error("removeUploadedPhoto failed:", e);
      toast.error("Could not remove photo. Try again.");
    } finally {
      setUploadingProof((prev) => ({ ...prev, [taskId]: false }));
    }
  };

  const clearUploadedLocalPreviews = (taskId, previewIds) => {
    setLocalPreviews((prev) => {
      const arr = prev[taskId] || [];

      arr.forEach((p) => {
        if (previewIds.includes(p.id) && p?.url?.startsWith("blob:")) {
          URL.revokeObjectURL(p.url);
        }
      });

      return {
        ...prev,
        [taskId]: arr.filter((p) => !previewIds.includes(p.id)),
      };
    });
  };

  const removeLocalPreview = (taskId, previewId) => {
    setLocalPreviews((prev) => {
      const arr = prev[taskId] || [];
      const target = arr.find((x) => x.id === previewId);
      if (target?.url?.startsWith("blob:")) URL.revokeObjectURL(target.url);

      return {
        ...prev,
        [taskId]: arr.filter((x) => x.id !== previewId),
      };
    });
  };

  // ✅ Create company notification: Task Submitted
  const pushTaskSubmittedNotification = async (task) => {
    if (!companyId || !task?.id || !user?.uid) return;

    try {
      const notifRef = collection(db, `companies/${companyId}/notifications`);

      await addDoc(notifRef, {
        type: "taskSubmitted", // ✅ matches Settings preference key
        timestamp: serverTimestamp(), // ✅ matches NotificationModal

        // Who
        employeeId: user.uid,
        employeeEmail: user.email || null,

        // What
        taskId: task.id,
        taskTitle: task.title || "Untitled task",
        taskType: task.type || null,
        priority: task.priority || null,

        // Context (optional but useful)
        workSiteName: task.workSiteName || null,
        dueDateISO: task.dueDateISO || null,

        // For UI usage later (optional)
        // readBy: {},  // you can add this pattern later if you track reads per user
        // route: `/task-manager?taskId=${task.id}`,
      });
    } catch (e) {
      // non-blocking — task submission should still succeed
      console.error("pushTaskSubmittedNotification failed:", e);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Status updates + Review flow
  // completed = submitted-for-review
  // reviewStatus: not_submitted | submitted | approved | rejected
  // ─────────────────────────────────────────────────────────────
  const updateStatus = async (task, nextStatus) => {
    if (!companyId || !task?.id) return;
    if (!STATUSES.includes(nextStatus)) return;

    const reviewStatus = task.reviewStatus || "not_submitted";
    const lockedForReview =
      reviewStatus === "submitted" || reviewStatus === "approved";

    // Block changing state while submitted/approved
    if (lockedForReview && nextStatus !== "completed") {
      toast.error(
        "This task is submitted for review and cannot be changed right now."
      );
      return;
    }

    // Enforce proof requirement on submit
    if (nextStatus === "completed" && task.photoRequired) {
      const existing = normalizeExistingPhotos(task);
      if (!existing.length) {
        toast.error("Photo proof is required before submitting this task.");
        return;
      }
    }

    // Enforce checklist completion on submit (only if checklist exists)
    if (nextStatus === "completed" && task.checklistEnabled) {
      const items = Array.isArray(task.checklist) ? task.checklist : [];
      if (items.length) {
        const allDone = items.every((it) => !!it.isDone);
        if (!allDone) {
          toast.error("Please complete all checklist items before submitting.");
          return;
        }
      }
    }

    try {
      const payload = {
        status: nextStatus,
        updatedAt: serverTimestamp(),
      };

      // When employee clicks "Submit for review"
      if (nextStatus === "completed") {
        payload.completedAt = serverTimestamp();
        payload.completedBy = user.uid;

        payload.reviewStatus = "submitted";
        payload.submittedAt = serverTimestamp();
        payload.submittedBy = user.uid;

        // Clear rejection banner (if resubmitting)
        payload.rejectionReason = null;
        payload.reviewedAt = null;
        payload.reviewedBy = null;
      }

      await updateDoc(
        doc(db, `companies/${companyId}/tasks/${task.id}`),
        payload
      );

      // ✅ Only fire notification when employee SUBMITS for review
      if (nextStatus === "completed") {
        await pushTaskSubmittedNotification(task);
      }

      const shouldToastSuccess =
        nextStatus === "completed" || nextStatus === "in_progress";

      if (shouldToastSuccess) {
        notify.success(
          nextStatus === "completed"
            ? "Submitted for employer review."
            : "Task started."
        );
      }
    } catch (e) {
      console.error("updateStatus failed:", e);
      toast.error("Could not update task. Try again.");
    }
  };

  // Modal Start button behavior:
  // Start task then CLOSE modal to “manage space”
  const startFromModal = async (task) => {
    await updateStatus(task, "in_progress");
  };

  if (loadingInit) {
    return (
      <div className="empTasks-page">
        <div className="empTasks-loading">Loading your tasks…</div>
      </div>
    );
  }

  // Helpers for UI labels
  const getReviewLabel = (reviewStatus) => {
    if (reviewStatus === "submitted") return "Under review";
    if (reviewStatus === "approved") return "Approved";
    if (reviewStatus === "rejected") return "Rejected";
    return "Not submitted";
  };

  return (
    <div className="empTasks-page">
      <ToastContainer />
      <nav className="empTasks-nav">
        <p className="empTasks-breadcrumb">
          <Link to="/employee-dashboard">Dashboard</Link> /{" "}
          <span>My Tasks</span>
        </p>
      </nav>

      <header className="empTasks-header">
        <div>
          <h1>My Tasks</h1>
          <p>Tasks assigned to you by your employer.</p>
        </div>

        <div className="empTasks-filters">
          <div className="empTasks-filter">
            <label className="empTasks-label">Status</label>
            <select
              className="empTasks-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="open">Open</option>
              <option value="in_progress">In progress</option>
              <option value="completed">Submitted</option>
            </select>
          </div>

          <div className="empTasks-filter">
            <label className="empTasks-label">Priority</label>
            <select
              className="empTasks-select"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>
      </header>

      {/* CARD LIST */}
      <section className="empTasks-card">
        {loadingTasks ? (
          <div className="empTasks-empty">Loading tasks…</div>
        ) : visibleTasks.length === 0 ? (
          <div className="empTasks-empty">No tasks found for this filter.</div>
        ) : (
          <div className="empTasks-list">
            {visibleTasks.map((t) => {
              const status = (t.status || "open").toLowerCase();
              const overdue = isOverdue(t);

              const reviewStatus = t.reviewStatus || "not_submitted";
              const isSubmitted = reviewStatus === "submitted";
              const isApproved = reviewStatus === "approved";
              const isRejected = reviewStatus === "rejected";

              const needsProof = !!t.photoRequired;
              const uploadedPhotos = normalizeExistingPhotos(t);
              const hasProof = uploadedPhotos.length > 0;

              return (
                <div
                  key={t.id}
                  className={`empTasks-item empTasks-item--clickable ${
                    overdue ? "overdue" : ""
                  }`}
                  role="button"
                  tabIndex={0}
                  onClick={() => openTaskModal(t.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") openTaskModal(t.id);
                  }}
                  title="Click to open task"
                >
                  <div className="empTasks-top">
                    <div className="empTasks-title">
                      {t.title || "Untitled task"}
                    </div>

                    <div className="empTasks-badges">
                      <span
                        className={`empTasks-pill pri-${String(
                          t.priority || ""
                        ).toLowerCase()}`}
                      >
                        {t.priority || "—"}
                      </span>

                      <span className="empTasks-pill type">
                        {t.type || "—"}
                      </span>

                      <span className={`empTasks-pill status status-${status}`}>
                        {status.replace("_", " ")}
                      </span>

                      <span
                        className={`empTasks-pill review review-${reviewStatus}`}
                      >
                        {getReviewLabel(reviewStatus)}
                      </span>

                      {needsProof && (
                        <span className="empTasks-pill proof">
                          {hasProof
                            ? `${uploadedPhotos.length} photo(s)`
                            : "Photo required"}
                        </span>
                      )}

                      {overdue && (
                        <span className="empTasks-pill overdue">Overdue</span>
                      )}
                    </div>
                  </div>

                  {/* Compact meta row */}
                  <div className="empTasks-meta">
                    <div>
                      <strong>Job site:</strong> {t.workSiteName || "—"}
                    </div>
                    <div>
                      <strong>Assigned:</strong> {formatAssigned(t)}
                    </div>
                    <div>
                      <strong>Due:</strong> {formatDue(t)}
                    </div>
                  </div>

                  {/* Small state hint */}
                  {status === "open" && !isSubmitted && !isApproved && (
                    <div className="empTasks-cardHint2">
                      Click to view and start.
                    </div>
                  )}
                  {status === "in_progress" && !isSubmitted && !isApproved && (
                    <div className="empTasks-cardHint2">
                      Started — click to finish & submit.
                    </div>
                  )}
                  {isSubmitted && (
                    <div className="empTasks-cardHint3">
                      Waiting for employer review.
                    </div>
                  )}
                  {isRejected && (
                    <div className="empTasks-cardHint">
                      Rejected — click to fix & resubmit.
                    </div>
                  )}
                  {isApproved && (
                    <div className="empTasks-cardHint1">
                      Approved — finalized.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ✅ TASK MODAL */}
      {showTaskModal &&
        activeTask &&
        (() => {
          const t = activeTask;

          const status = (t.status || "open").toLowerCase();
          const overdue = isOverdue(t);

          const reviewStatus = t.reviewStatus || "not_submitted";
          const isSubmitted = reviewStatus === "submitted";
          const isApproved = reviewStatus === "approved";
          const isRejected = reviewStatus === "rejected";
          const lockedForReview = isSubmitted || isApproved;

          const {
            enabled: checklistEnabled,
            items: checklistItems,
          } = normalizeChecklist(t);
          const checklistProg = getChecklistProgress(checklistItems);
          const hasChecklist = checklistEnabled && checklistItems.length > 0;

          const needsProof = !!t.photoRequired;
          const uploadedPhotos = normalizeExistingPhotos(t);
          const hasProof = uploadedPhotos.length > 0;

          const isUploading = !!uploadingProof[t.id];
          const taskLocalPreviews = localPreviews[t.id] || [];

          const checklistOk = !hasChecklist || checklistProg.allDone;
          const canSubmit = (!needsProof || hasProof) && checklistOk;

          const canEdit = !lockedForReview;

          const showProofArea =
            needsProof &&
            ((status === "in_progress" && canEdit) ||
              (isRejected && status === "in_progress"));

          const canShowComment = status === "in_progress" && !isApproved;
          const canEditComment = canEdit && status === "in_progress";

          return (
            <div
              className="empTasks-modal-backdrop"
              onMouseDown={(e) => {
                if (e.target.classList.contains("empTasks-modal-backdrop"))
                  closeTaskModal();
              }}
            >
              <div className="empTasks-modal" role="dialog" aria-modal="true">
                <div className="empTasks-modal-header">
                  <div>
                    <h3 className="empTasks-modal-title">
                      {t.title || "Untitled task"}
                    </h3>
                    <p className="empTasks-modal-subtitle">
                      Job site: <strong>{t.workSiteName || "—"}</strong> •
                      Assigned: <strong>{formatAssigned(t)}</strong> • Due:{" "}
                      <strong>{formatDue(t)}</strong>
                    </p>
                  </div>

                  <button
                    type="button"
                    className="empTasks-icon-btn"
                    onClick={closeTaskModal}
                    aria-label="Close"
                    title="Close"
                  >
                    ✕
                  </button>
                </div>

                <div className="empTasks-modal-body">
                  {/* Pills */}
                  <div className="empTasks-detailPills">
                    <span className={`empTasks-pill status status-${status}`}>
                      {status.replace("_", " ")}
                    </span>
                    <span className="empTasks-pill type">{t.type || "—"}</span>
                    <span
                      className={`empTasks-pill pri-${String(
                        t.priority || ""
                      ).toLowerCase()}`}
                    >
                      {t.priority || "—"}
                    </span>
                    <span
                      className={`empTasks-pill review review-${reviewStatus}`}
                    >
                      {getReviewLabel(reviewStatus)}
                    </span>
                    {needsProof && (
                      <span className="empTasks-pill proof">
                        {hasProof
                          ? `${uploadedPhotos.length} photo(s)`
                          : "Photo required"}
                      </span>
                    )}
                    {overdue && (
                      <span className="empTasks-pill overdue">Overdue</span>
                    )}
                  </div>

                  {/* Banners */}
                  {isSubmitted && (
                    <div className="empTasks-reviewBanner submitted">
                      Submitted for review — waiting for employer approval.
                    </div>
                  )}
                  {isRejected && (
                    <div className="empTasks-reviewBanner rejected">
                      Rejected —{" "}
                      {t.rejectionReason
                        ? t.rejectionReason
                        : "Please fix and resubmit."}
                    </div>
                  )}
                  {isApproved && (
                    <div className="empTasks-reviewBanner approved">
                      Approved — task is finalized.
                    </div>
                  )}

                  {/* Description */}
                  {t.description ? (
                    <div className="empTasks-desc">
                      <p>Task Description:</p>
                      {t.description}
                    </div>
                  ) : null}

                  {/* Checklist */}
                  {hasChecklist && (
                    <div className="empTasks-checklist">
                      <div className="empTasks-checklistTop">
                        <div className="empTasks-checklistTitle">Checklist</div>
                        <div className="empTasks-checklistProgress">
                          {checklistProg.done}/{checklistProg.total} done
                        </div>
                      </div>

                      <div className="empTasks-checklistList">
                        {checklistItems.map((it) => (
                          <label
                            key={it.id}
                            className={`empTasks-checkRow ${
                              it.isDone ? "done" : ""
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={!!it.isDone}
                              disabled={lockedForReview}
                              onChange={() => toggleChecklistItem(t, it.id)}
                            />
                            <span className="empTasks-checkText">
                              {it.text}
                            </span>
                          </label>
                        ))}
                      </div>

                      {lockedForReview && (
                        <div className="empTasks-checklistHint">
                          Checklist is locked while the task is under review.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Proof */}
                  {showProofArea && (
                    <div className="empTasks-proof">
                      <div className="empTasks-proofTop">
                        <div className="empTasks-proofTitle">
                          Photo proof {hasProof ? "(uploaded)" : "(required)"}
                        </div>

                        <label className="empTasks-proofPick">
                          {isUploading ? "Uploading…" : "Add photo(s)"}
                          <input
                            ref={(el) => (fileInputRefs.current[t.id] = el)}
                            type="file"
                            accept="image/*"
                            multiple
                            disabled={isUploading}
                            onChange={(e) =>
                              onSelectProofFiles(t, e.target.files)
                            }
                          />
                        </label>
                      </div>

                      <div className="empTasks-proofHint">
                        Selecting a photo uploads it automatically. You can add
                        more photos anytime.
                      </div>

                      {/* Local previews */}
                      {taskLocalPreviews.length > 0 && (
                        <div className="empTasks-proofGrid">
                          {taskLocalPreviews.map((p) => (
                            <div key={p.id} className="empTasks-thumb">
                              <img
                                src={p.url}
                                alt="Selected proof"
                                className="empTasks-thumbImg"
                              />

                              <div className="empTasks-thumbMeta">
                                <span
                                  className={`empTasks-thumbStatus ${p.status}`}
                                >
                                  {p.status === "uploading"
                                    ? "Uploading…"
                                    : p.status === "uploaded"
                                    ? "Uploaded"
                                    : p.status === "error"
                                    ? "Failed"
                                    : "Ready"}
                                </span>

                                <button
                                  type="button"
                                  className="empTasks-thumbRemove"
                                  onClick={() => removeLocalPreview(t.id, p.id)}
                                  title="Remove preview"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Uploaded photos */}
                      {uploadedPhotos.length > 0 && (
                        <div className="empTasks-proofGrid">
                          {uploadedPhotos.map((ph, idx) => (
                            <div
                              key={`${ph.url}_${idx}`}
                              className="empTasks-thumb"
                            >
                              <a
                                href={ph.url}
                                target="_blank"
                                rel="noreferrer"
                                title="Open full image"
                              >
                                <img
                                  src={ph.url}
                                  alt="Uploaded proof"
                                  className="empTasks-thumbImg"
                                />
                              </a>

                              <div className="empTasks-thumbMeta">
                                <span className="empTasks-thumbStatus uploaded">
                                  Uploaded
                                </span>

                                <button
                                  type="button"
                                  className="empTasks-thumbRemove"
                                  onClick={() => removeUploadedPhoto(t, idx)}
                                  disabled={isUploading}
                                  title="Remove uploaded photo"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Comment */}
                  {canShowComment && (
                    <div className="empTasks-comment">
                      <label className="empTasks-commentLabel">
                        Optional comment
                      </label>

                      <textarea
                        className="empTasks-commentInput"
                        placeholder="Add a quick note for your employer (optional)…"
                        value={draftComments[t.id] ?? ""}
                        disabled={
                          isUploading ||
                          !!savingComment[t.id] ||
                          !canEditComment
                        }
                        onChange={(e) =>
                          setDraftComments((prev) => ({
                            ...prev,
                            [t.id]: e.target.value,
                          }))
                        }
                      />

                      <div className="empTasks-commentActions">
                        <button
                          type="button"
                          className="empTasks-btn ghost"
                          disabled={
                            isUploading ||
                            !!savingComment[t.id] ||
                            !canEditComment
                          }
                          onClick={() =>
                            setDraftComments((prev) => ({
                              ...prev,
                              [t.id]: t.completionComment || "",
                            }))
                          }
                        >
                          Reset
                        </button>

                        <button
                          type="button"
                          className="empTasks-btn primary"
                          disabled={
                            isUploading ||
                            !!savingComment[t.id] ||
                            !canEditComment
                          }
                          onClick={() => saveComment(t)}
                        >
                          {savingComment[t.id] ? "Saving…" : "Save comment"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* ACTIONS — Start lives INSIDE modal and closes modal */}
                <div className="empTasks-modal-footer">
                  <button
                    type="button"
                    className="empTasks-btn ghost"
                    onClick={closeTaskModal}
                  >
                    Close
                  </button>

                  {status === "open" && !lockedForReview && (
                    <button
                      type="button"
                      className="empTasks-btn primary"
                      onClick={() => startFromModal(t)}
                    >
                      Start
                    </button>
                  )}

                  {status === "in_progress" && (
                    <>
                      <button
                        type="button"
                        className="empTasks-btn ghost"
                        onClick={() => updateStatus(t, "open")}
                        disabled={isUploading || lockedForReview}
                      >
                        Move back
                      </button>

                      <button
                        type="button"
                        className="empTasks-btn primary"
                        onClick={() => updateStatus(t, "completed")}
                        disabled={
                          !canSubmit || isUploading || isSubmitted || isApproved
                        }
                        title={
                          isSubmitted
                            ? "Already submitted for review."
                            : isApproved
                            ? "This task is approved and finalized."
                            : !checklistOk
                            ? "Complete all checklist items before submitting."
                            : !canSubmit
                            ? "Upload at least 1 photo proof before submitting."
                            : isUploading
                            ? "Please wait for upload to finish."
                            : "Submit for review"
                        }
                        style={{
                          opacity:
                            !canSubmit ||
                            isUploading ||
                            isSubmitted ||
                            isApproved
                              ? 0.6
                              : 1,
                          cursor:
                            !canSubmit ||
                            isUploading ||
                            isSubmitted ||
                            isApproved
                              ? "not-allowed"
                              : "pointer",
                        }}
                      >
                        {isRejected ? "Resubmit" : "Submit for review"}
                      </button>
                    </>
                  )}

                  {/* No employee reopen after submitted/approved */}
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
}
